import React, { FC, ReactNode, useMemo, useState } from 'react';
import { Label, Typography } from '@rbx/ui';
import ResponsiveTooltip from '@modules/miscellaneous/common/components/ResponsiveTooltip';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsEngineReferenceEvent from '@modules/eventStream/enum/DocsEngineReferenceEvent';
import { ApiTag, CloudTag, EngineTag, ReferenceClass } from '@modules/ui/tags/types';
import useReferenceSummaryMemberTableStyles from './ReferenceSummaryMemberTable.styles';
import useReferenceSummaryMemberTableStylesV2 from './ReferenceSummaryMemberTableV2.styles';
import tooltipUXProps from '../../constants/tooltipUXProps';

const tagsWithWarningSeverity: Array<EngineTag | CloudTag> = [
  ApiTag.Deprecated,
  CloudTag.Required,
  CloudTag.Experimental,
];
const tagsWithInfoSeverity: Array<EngineTag | CloudTag> = [
  CloudTag.Required,
  CloudTag.InputOnly,
  CloudTag.Stable,
];
const tagsWithSuccessSeverity: Array<EngineTag | CloudTag> = [CloudTag.OutputOnly, CloudTag.Beta];
const getTagSeverity = (tag: EngineTag | CloudTag): 'warning' | 'info' | 'default' | 'success' => {
  if (tagsWithWarningSeverity.includes(tag)) return 'warning';
  if (tagsWithInfoSeverity.includes(tag)) return 'info';
  if (tagsWithSuccessSeverity.includes(tag)) return 'success';
  return 'default';
};

export type ReferenceMemberTagProps = {
  label: string;
  description: ReactNode | string;
  tag: EngineTag | CloudTag;
  referenceClass: ReferenceClass;
  overrideSeverity?: 'warning' | 'info' | 'default' | 'success';
  engineRevampEnabled?: boolean;
};

const ReferenceMemberTag: FC<React.PropsWithChildren<ReferenceMemberTagProps>> = ({
  label,
  description,
  tag,
  referenceClass,
  overrideSeverity,
  engineRevampEnabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const defaultStyles = useReferenceSummaryMemberTableStyles();
  const v2Styles = useReferenceSummaryMemberTableStylesV2();
  const {
    classes: { tagWrapper, tagLabel, tagTooltip },
    cx,
  } = engineRevampEnabled ? v2Styles : defaultStyles;
  const tooltipTrigger = useMemo(
    () => (
      <span className={cx(tagWrapper, referenceClass)}>
        <Label
          key={label}
          aria-label={label}
          variant='contained'
          className={tagLabel}
          severity={overrideSeverity ?? getTagSeverity(tag)}
          labelText={label}
        />
      </span>
    ),
    [cx, tagLabel, tagWrapper, referenceClass, label, tag, overrideSeverity],
  );
  return description ? (
    <ResponsiveTooltip
      key={label}
      data-tooltip={label}
      aria-label={label}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onOpen={() => {
        unifiedLoggerClient.logImpressionEvent({
          eventName: DocsEngineReferenceEvent.OpenTypeTooltip,
          parameters: {
            label,
            tag: String(tag),
          },
        });
      }}
      arrow
      placement='bottom'
      title={
        <Typography component='div' variant='body2' className={tagTooltip}>
          {description}
        </Typography>
      }
      classes={{
        popper: tagTooltip,
      }}
      engineRevampEnabled={engineRevampEnabled}
      {...tooltipUXProps}>
      {tooltipTrigger}
    </ResponsiveTooltip>
  ) : (
    tooltipTrigger
  );
};

export default ReferenceMemberTag;
